import React, { useState, useEffect } from 'react';
import linq from 'linq';
import styled from 'styled-components'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { IconButton } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ImportContactsIcon from '@material-ui/icons/ImportContacts';
import ViewAgendaOutlinedIcon from '@material-ui/icons/ViewAgendaOutlined';
import MapIcon from '@material-ui/icons/Map';
import GridOnIcon from '@material-ui/icons/GridOn';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { useDispatch } from 'react-redux'

import { SheetKinds, SheetDb, setDefaultSheet } from '../redux/sheetDbSlice';
import { SheetTreeDb, updateSheetTree } from '../redux/sheetTreeDbSlice';
import { updateUserCondition } from '../redux/userConditionSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useUserCondition from '../hooks/useUserCondition';
import useOneBookDb from '../hooks/useOneBookDb';
import { SheetNodeType, SheetListHash } from '../hooks/useCurrentTreeHash';

import { deepCopy } from '../general';
import { WorkAreaStateType } from './BookEditor'
import { TreeMenuAnchorType } from './TreeMenu';

const BookTreeDiv = styled.div`
  position: relative;
  margin-left: 8px;
  z-index: 0;
`
type BookNodeDivProps = {
  nodeHeight: number;
  selecting: boolean;
}
const BookNodeDiv = styled.div<BookNodeDivProps>`
  height: ${props => props.nodeHeight + 'px'};
  background-color: ${props => props.selecting ? '#eeeeee' : undefined};
  cursor: pointer;
`
type SheetNodeWrapperProps = {
  grabbing: boolean;
}
const SheetNodeWrapper = styled.div<SheetNodeWrapperProps>`
  position: absolute;
  width: 100%;
  z-index: ${props => props.grabbing ? 2 : 1};
`
type SheetNodeDivProps = {
  nodeHeight: number;
  xValue: number;
  yValue: number;
  selecting: boolean;
  dragging: boolean;
  grabbing: boolean;
  otherGrabbing: boolean;
}
const SheetNodeDiv = styled.div<SheetNodeDivProps>`
  position: absolute;
  width: 100%;
  height: ${props => props.nodeHeight + 'px'};
  background-color: ${props => props.selecting ? '#e0e0e08a' : undefined};
  transform: ${props => 'translate(' + props.xValue + 'px, ' + props.yValue + 'px) !important'};
  transition: ${props => props.dragging ? undefined : '300ms'};
  cursor: ${props => props.grabbing ? 'grabbing' : 'pointer'};
  pointer-events: ${props => props.otherGrabbing ? 'none' : undefined};
`
type OverlapDisplayProps = {
  nodeHeight: number;
  nodeLength: number;
  xValue: number;
  yValue: number;
  overlapped: boolean;
}
const OverlapDisplay = styled.div<OverlapDisplayProps>`
  position: absolute;
  width: 100%;
  height: ${props => props.nodeLength * props.nodeHeight + 'px'};
  background-color: ${props => props.overlapped ? '#eeeeee' : undefined};
  transform: ${props => 'translate(' + props.xValue + 'px, ' + props.yValue + 'px) !important'};
  z-index: 0;
  pointer-events: none;
`
type ExpandIconProps = {
  show: boolean;
  fold: boolean;
  nodeHeight: number;
  xValue: number;
  yValue: number;
  anyFolding: boolean;
  otherGrabbing: boolean;
}
const ExpandIcon = styled(ExpandMoreIcon)<ExpandIconProps>`
  position: absolute;
  opacity: ${props => props.show ? 1 : 0};
  font-size: ${props => props.nodeHeight * 3 / 4 + 'px'};
  transform: ${props => {
    const rotateValue = props.fold ? 'rotate(-90deg)' : '';
    return 'translate(' + props.xValue + 'px, ' + props.yValue + 'px) ' + rotateValue + ' !important';
  }};
  transition: ${props => props.anyFolding || props.otherGrabbing ? '300ms' : undefined};
  transition-property: ${props => props.anyFolding || props.otherGrabbing ? 'transform, opacity' : undefined};
  cursor: pointer;
`

type FoldedType = { childrenLength: number; descendantLength: number; }
export type FoldedHash = { [key in string]: FoldedType }

const nodeHeight = 24;
const getHeight = (ids: string[], list: SheetListHash, foldedIds: string[]) => {
  const margin = 48;
  if (foldedIds.length === 0) return (ids.length + 1) * nodeHeight + margin;
  const exclusionIds: string[] = [];
  foldedIds.forEach(id => exclusionIds.push(...list[id].descendantIds))
  const uniqueExclusionIds = Array.from(new Set(exclusionIds));
  const shownLength = linq.from(ids).where(id => !uniqueExclusionIds.includes(id)).toArray().length + 1;
  return shownLength * nodeHeight + margin;
}

export const ToggleBookTreeButton: React.FC = () => {
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [conditionId, condition] = useUserCondition(userId);

  const toggleBookTree = () => {
    if (!conditionId || !condition) return;
    const nowValue = condition.bookTreeDisplay;
    dispatch(updateUserCondition({ id: conditionId, bookTreeDisplay: !nowValue }));
  }

  if (!condition) return <></>;
  return (
    <IconButton data-html2canvas-ignore="true" size="small" onClick={() => toggleBookTree()}>
      { condition.bookTreeDisplay ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon /> }
    </IconButton>
  );
}

type SheetTreeProps = {
  parentId: string;
  deepness: number;
  ancesterIds: string[];
  ids: string[];
  originList: SheetListHash;
  list: SheetListHash;
  currentSheetId: string | null;
  selectSheet: (id: string) => void;
  handleStart: (id: string, sheet: SheetNodeType) => void;
  draggingId: string | undefined;
  unfoldingId: string | undefined;
  anyFolding: boolean;
  changeList: (id: string, data: DraggableData) => void;
  changeData: (id: string, data: DraggableData) => void;
  folded: string[];
  toggleFolded: (id: string) => void;
  openTreeMenu: (value: TreeMenuAnchorType) => void;
}
const SheetTree: React.FC<SheetTreeProps> = ({ parentId, deepness, ancesterIds, ids, originList, list, currentSheetId, selectSheet, handleStart, draggingId, unfoldingId, anyFolding, changeList, changeData, folded, toggleFolded, openTreeMenu }) => {
  const show = !ancesterIds.some(id => folded.includes(id) && id !== unfoldingId);

  const getIcon = (kind: SheetKinds) => {
    if (kind === 'story') return <ViewAgendaOutlinedIcon style={{fontSize: nodeHeight * 3 / 4, verticalAlign: -nodeHeight / 8 + 'px', marginRight: nodeHeight / 4}} />;
    if (kind === 'map') return <MapIcon style={{fontSize: nodeHeight * 3 / 4, verticalAlign: -nodeHeight / 8 + 'px', marginRight: nodeHeight / 4}} />;
    if (kind === 'grid') return <GridOnIcon style={{fontSize: nodeHeight * 3 / 4, verticalAlign: -nodeHeight / 8 + 'px', marginRight: nodeHeight / 4}} />;
  }

  return (
    <div style={{visibility: show ? 'visible' : 'hidden', opacity: show ? 1 : 0, transition: 'opacity 300ms', zIndex: show ? 'auto' : -1}}>
    { ids.map(id => {
      const originSheet = originList[id];
      const sheet = list[id];
      if (!originSheet || !sheet || sheet.parentId !== parentId) return undefined;
      return (
        <Draggable key={id} position={{x: 0, y: 0}} handle={'.handle' + deepness}
          onStart={() => handleStart(id, sheet)}
          onDrag={(ev: DraggableEvent, data: DraggableData) => changeList(id, data)}
          onStop={(ev: DraggableEvent, data: DraggableData) => changeData(id, data)}
          bounds={{left: undefined, top: undefined, right: undefined, bottom: undefined}}
        >
          <SheetNodeWrapper grabbing={id === draggingId}>
            { (show && originSheet.descendantIds.length >= 1) &&
              <ExpandIcon
                show={originSheet.descendantIds.length >= 1}
                fold={folded.includes(id)}
                nodeHeight={nodeHeight}
                xValue={(deepness - 1) * nodeHeight / 2 - 4}
                yValue={sheet.appearanceOrder * nodeHeight + nodeHeight / 8}
                anyFolding={anyFolding} otherGrabbing={draggingId !== undefined && id !== draggingId}
                onClick={() => toggleFolded(id)}
              />
            }
            <SheetNodeDiv className={'handle' + deepness}
              nodeHeight={nodeHeight}
              xValue={deepness * nodeHeight / 2} yValue={sheet.appearanceOrder * nodeHeight}
              selecting={id === currentSheetId}
              dragging={id === draggingId || draggingId === undefined}
              grabbing={id === draggingId} otherGrabbing={draggingId !== undefined && id !== draggingId}
              onContextMenu={ev => openTreeMenu({ open: true, anchorPosition: { left: ev.pageX, top: ev.pageY }, data: { id, sheet: originSheet } })}
            >
              { getIcon(sheet.sheetKind) }
              { sheet.name }
            </SheetNodeDiv>
            { (sheet.descendantIds.length > 0) &&
              <SheetTree parentId={id} deepness={deepness + 1}
                ancesterIds={[...ancesterIds, id]}
                ids={sheet.descendantIds}
                originList={originList} list={list}
                currentSheetId={currentSheetId}
                selectSheet={selectSheet}
                handleStart={handleStart}
                draggingId={draggingId} unfoldingId={unfoldingId}
                anyFolding={anyFolding}
                changeList={changeList} changeData={changeData}
                folded={folded} toggleFolded={toggleFolded}
                openTreeMenu={openTreeMenu}
              />
            }
          </SheetNodeWrapper>
        </Draggable>
      );
    }) }
    </div>
  )
}

type BookTreeProps = {
  workAreaState: WorkAreaStateType;
  bookId: string | null;
  currentSheetId: string | null;
  sheetIds: string[];
  sheets: SheetDb[];
  sheetTrees: SheetTreeDb[];
  folded: FoldedHash;
  setFolded: (folded: FoldedHash) => void;
  sheetListValue: SheetListHash;
  nodeClicked?: (workArea: WorkAreaStateType, id?: string) => void;
  setMenuAnchor: (value?: TreeMenuAnchorType) => void;
}
const BookTree: React.FC<BookTreeProps> = ({ workAreaState, bookId, currentSheetId, sheetIds, sheets, sheetTrees, folded, setFolded, sheetListValue, nodeClicked, setMenuAnchor }) => {
  const dispatch = useDispatch();
  const book = useOneBookDb(bookId || undefined);
  const [originSheetList, setOriginSheetList] = useState<SheetListHash>({});
  const [sheetList, setSheetList] = useState<SheetListHash>({});
  const [ids, setIds] = useState<string[]>([]);
  const [rootSheetIds, setRootSheetIds] = useState<string[]>([]);
  const [foldedIds, setFoldedIds] = useState<string[]>([]);
  const [draggingId, setDraggingId] = useState<string | undefined>(undefined);
  const [anyFolding, setAnyFolding] = useState<boolean>(false);
  const [unfoldingId, setUnfoldingId] = useState<string | undefined>(undefined);
  type ParentDataType = { change: boolean; newId?: string; lastId?: string; }
  const [parentData, setParentData] = useState<ParentDataType>({ change: false });
  const [height, setHeight] = useState(getHeight(sheetIds, sheetListValue, foldedIds));

  useEffect(() => {
    setSheetList(sheetListValue);
    setOriginSheetList(sheetListValue);
    setIds(Object.keys(sheetListValue));
    setFoldedIds(Object.keys(folded));
  }, [sheets, sheetTrees, folded])

  useEffect(() => {
    const sheetListLength = Object.keys(sheetList).length;
    if (sheetListLength === 0) return;
    setRootSheetIds(sheetIds.filter(id => {
      const sheet = sheetList[id];
      if (sheet) return sheet.parentId === bookId;
      else return false;
    }));
  }, [sheetList])

  useEffect(() => setHeight(getHeight(sheetIds, sheetListValue, foldedIds)), [sheetIds, sheetListValue, foldedIds])

  if (!bookId || !book || !nodeClicked) return <></>;

  const toggleFolded = (id: string) => {
    const newFolded = deepCopy(folded);
    const sheet = sheetList[id];
    if (!sheet || sheet.descendantIds.length === 0) return;
    if (!foldedIds.includes(id)) {
      setDraggingId(id);
      setAnyFolding(true);
      const childrenIds = sheetIds.filter(targetId => sheetList[targetId] && sheetList[targetId].parentId === id);
      newFolded[id] = { childrenLength: childrenIds.length, descendantLength: sheet.descendantIds.length };
      setFolded(newFolded);

      setTimeout(() => {
        setDraggingId(undefined);
        setAnyFolding(false);
      }, 300)
    } else {
      setDraggingId(id);
      setUnfoldingId(id);
      setAnyFolding(true);
      delete newFolded[id];
      setFolded(newFolded);

      setTimeout(() => {
        setDraggingId(undefined);
        setUnfoldingId(undefined);
        setAnyFolding(false);
      }, 300)
    }
  }
  const openTreeMenu = (value: TreeMenuAnchorType) => {
    if (!value.data) return;
    setMenuAnchor(value);
    nodeClicked('sheet', value.data.id);
    dispatch(setDefaultSheet(value.data.id));
  }
  const selectSheet = (id: string) => {
    nodeClicked('sheet', id);
    setMenuAnchor();
    dispatch(setDefaultSheet(id));
  }
  const handleStart = (id: string, sheet: SheetNodeType) => {
    setDraggingId(id);
    setTimeout(() => {
      selectSheet(id);
      setParentData({ ...parentData, lastId: sheet.parentId });
    }, 1)
  }
  const changeList = (id: string, data: DraggableData) => {
    const sheet = sheetList[id];
    const newList = deepCopy(sheetList);
    const drag = sheet.appearanceOrder;
    const movement = Math.round(data.y / nodeHeight);
    const drop = drag + movement;
    const detailedDrop = drag + data.y / nodeHeight;
    const descendantLength = sheet.shownLength - 1;

    const maxDeepness = 1 + ids.reduce((accumulator, targetId) => {
      const target = sheetList[targetId];
      if (targetId === id || !target) return accumulator;
      return Math.max(target.deepness, accumulator);
    }, 0);
    const rawDropDeepness = sheet.deepness + Math.round(data.x / (nodeHeight / 2));
    const dropDeepness = rawDropDeepness < -1 ? -1 : rawDropDeepness > maxDeepness ? maxDeepness : rawDropDeepness;

    // update parentData
    const overlappedNominees: SheetListHash = {};
    const overlapCondition = detailedDrop > drag ? -0.5 : 0;
    const dropPointForOverlap = detailedDrop > drag ? detailedDrop + descendantLength : detailedDrop;
    ids.forEach(targetId => {
      const target = originSheetList[targetId] ? deepCopy(originSheetList[targetId]) : undefined;
      if (targetId === id || !target || sheet.descendantIds.includes(targetId)) return;

      const targetDescendantLength = target.shownLength - 1;
      const parentRangeTop = target.appearanceOrder + overlapCondition;
      const parentRangeBottom = parentRangeTop + 0.75 + targetDescendantLength;
      if (parentRangeTop <= dropPointForOverlap && dropPointForOverlap < parentRangeBottom && target.deepness <= dropDeepness) {
        if (!target.descendantIds.includes(id) && !foldedIds.includes(targetId)) {
          target.shownLength += 1 + descendantLength;
          newList[targetId].shownLength = target.shownLength;
          setSheetList(newList);
          overlappedNominees[targetId] = target;
        }
        else overlappedNominees[targetId] = target;
      }
      else if (parentRangeTop <= dropPointForOverlap && dropPointForOverlap < parentRangeBottom - 0.75) {
        if (!target.descendantIds.includes(id) && !foldedIds.includes(targetId)) {
          target.shownLength += 1 + descendantLength;
          newList[targetId].shownLength = target.shownLength;
          setSheetList(newList);
          overlappedNominees[targetId] = target;
        }
        else overlappedNominees[targetId] = target;
      }
    })

    const overlappedNomineeIds = Object.keys(overlappedNominees);

    if (overlappedNomineeIds.length >= 1) {
      overlappedNomineeIds.sort((a, b) => overlappedNominees[b].deepness - overlappedNominees[a].deepness);
      const targetId = overlappedNomineeIds[0];
      if (targetId !== parentData.lastId) setParentData({ ...parentData, change: true, newId: targetId });
      else setParentData({ ...parentData, change: false, newId: targetId });
    } else if (parentData.lastId !== bookId) {
      setParentData({ ...parentData, change: true, newId: bookId });
    } else {
      setParentData({ ...parentData, change: false, newId: bookId });
    }

    const sortedIds = deepCopy(ids).sort((a, b) => originSheetList[a].appearanceOrder - originSheetList[b].appearanceOrder);
    let appearanceOrderValue = 0;
    sortedIds.forEach(targetId => {
      const originTarget = originSheetList[targetId];

      const isFoldedAncestor = ids.some(ancestorId => {
        return foldedIds.includes(ancestorId) && originSheetList[ancestorId].descendantIds.includes(targetId);
      });
      if (isFoldedAncestor) {
        newList[targetId].appearanceOrder = appearanceOrderValue - 1;
        return;
      }

      if (!originTarget || sheet.descendantIds.includes(targetId)) return;
      if (targetId === id) {
        appearanceOrderValue += sheet.shownLength;
        return;
      }
      if (movement > 0) {
        if (drag < originTarget.appearanceOrder && originTarget.appearanceOrder <= drop + descendantLength) {
          newList[targetId].appearanceOrder = appearanceOrderValue - sheet.shownLength;
        }
        else newList[targetId].appearanceOrder = appearanceOrderValue;
      }
      if (movement < 0) {
        if (drop <= originTarget.appearanceOrder && originTarget.appearanceOrder <= drag && targetId !== parentData.newId) {
          newList[targetId].appearanceOrder = appearanceOrderValue + sheet.shownLength;
        }
        else newList[targetId].appearanceOrder = appearanceOrderValue;
      }
      if (movement === 0) {
        newList[targetId].appearanceOrder = originTarget.appearanceOrder;
      }
      appearanceOrderValue++;
    })

    setSheetList(newList);
  }
  const changeData = (id: string, data: DraggableData) => {
    const selectingSheet = originSheetList[id];
    const newList = deepCopy(originSheetList);
    const drag = selectingSheet.appearanceOrder;
    const movement = Math.round(data.y / nodeHeight);
    const detailedMovement = data.y / nodeHeight;
    const drop = drag + movement;
    const descendantLength = selectingSheet.shownLength - 1;

    let newOrder = 0;
    ids.forEach(targetId => {
      const target = originSheetList[targetId];
      if (!target || targetId === id || target.shownLength === 0) return;
      const targetOrderBottom = target.appearanceOrder + target.shownLength - 1;
      if (detailedMovement > 0 && !parentData.change) {
        if (target.parentId !== parentData.lastId) return;
        if (drag < target.appearanceOrder && targetOrderBottom <= drop + descendantLength) {
          newList[targetId].order--;
          newList[id].order++;
        }
      }
      if (detailedMovement < 0 && !parentData.change) {
        if (target.parentId !== parentData.lastId) return;
        if (drop <= target.appearanceOrder && target.appearanceOrder < drag) {
          newList[targetId].order++;
          newList[id].order--;
        }
      }
      if (detailedMovement > 0 && parentData.change) {
        if (targetId === parentData.newId) {
          if (foldedIds.includes(targetId)) newList[id] = { ...selectingSheet, parentId: parentData.newId, order: folded[targetId].childrenLength };
          else newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
        }
        if (target.parentId === parentData.lastId) {
          if (targetOrderBottom > drag) newList[targetId].order--;
        }
        else if (target.parentId === parentData.newId) {
          if (targetOrderBottom > drop + descendantLength) newList[targetId].order++;
          else if (targetOrderBottom <= drop + descendantLength) {
            newOrder++;
            newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
          }
        }
        
      }
      if (detailedMovement < 0 && parentData.change) {
        if (targetId === parentData.newId) {
          if (foldedIds.includes(targetId)) newList[id] = { ...selectingSheet, parentId: parentData.newId, order: folded[targetId].childrenLength };
          else newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
        }
        if (target.parentId === parentData.lastId) {
          if (target.appearanceOrder > drag) newList[targetId].order--;
        }
        else if (target.parentId === parentData.newId) {
          if (target.appearanceOrder >= drop) {
            newList[targetId].order++;
            newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
          }
          else if (target.appearanceOrder < drop) {
            newOrder++;
            newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
          }
        }
      }
      if (detailedMovement === 0 && parentData.change) {
        if (targetId === parentData.newId) newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
        if (target.parentId === parentData.lastId) {
          if (targetOrderBottom > drag) newList[targetId].order--;
        }
        else if (target.parentId === parentData.newId) {
          if (targetOrderBottom > drop + descendantLength) newList[targetId].order++;
          else if (targetOrderBottom <= drop + descendantLength) {
            newOrder++;
            newList[id] = { ...selectingSheet, parentId: parentData.newId, order: newOrder };
          }
        }
      }
    })

    ids.forEach(targetId => {
      const target = newList[targetId];
      if (!target) return;
      dispatch(updateSheetTree({id: target.treeId, order: target.order, parentSheetId: target.parentId === bookId ? undefined : target.parentId}));
    })
    setDraggingId(undefined);
    setParentData({ change: false });
  }

  return (
    <BookTreeDiv style={{ height }}>
      <BookNodeDiv onClick={() => nodeClicked('bookCover')}
        nodeHeight={nodeHeight} selecting={workAreaState === 'bookCover'}>
        <ImportContactsIcon style={{fontSize: nodeHeight * 3 / 4, verticalAlign: -nodeHeight / 8 + 'px', marginRight: nodeHeight / 4}} />
        { book.name }
      </BookNodeDiv>
      <SheetTree parentId={bookId} deepness={1}
        ancesterIds={[]}
        ids={rootSheetIds}
        originList={originSheetList} list={sheetList}
        currentSheetId={workAreaState !== 'bookCover' ? currentSheetId : null}
        selectSheet={selectSheet}
        handleStart={handleStart}
        draggingId={draggingId} unfoldingId={unfoldingId}
        anyFolding={anyFolding}
        changeList={changeList} changeData={changeData}
        folded={foldedIds} toggleFolded={toggleFolded}
        openTreeMenu={openTreeMenu}
      />
      { ids.map(id => {
        const sheet = sheetList[id];
        if (!sheet || sheet.shownLength === 0) return undefined;
        return (
          <OverlapDisplay key={id}
            nodeHeight={nodeHeight}
            nodeLength={sheet.shownLength}
            xValue={sheet.deepness * 12} yValue={sheet.appearanceOrder * nodeHeight}
            overlapped={id === parentData.newId}
          />
        );
      }) }
    </BookTreeDiv>
  );
}

export default BookTree;
